"use client";

import React, { useEffect, useRef, useState } from "react";
import { Play, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function YourPartnerInLifelongHealth() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="175584" id="understanding-your-sleep" className="relative flex min-h-[90vh] items-center overflow-hidden bg-background py-20 lg:py-32">
      {}
      <style dangerouslySetInnerHTML={{
      __html: `
          @keyframes blob {
            0% { transform: translate(0px, 0px) scale(1); }
            33% { transform: translate(30px, -50px) scale(1.1); }
            66% { transform: translate(-20px, 20px) scale(0.9); }
            100% { transform: translate(0px, 0px) scale(1); }
          }
          .animate-blob {
            animation: blob 10s infinite alternate;
          }
          .animation-delay-2000 {
            animation-delay: 2s;
          }
          .animation-delay-4000 {
            animation-delay: 4s;
          }
        `
    }} />

      {}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div className="animate-blob absolute top-0 left-1/4 h-[500px] w-[500px] rounded-full bg-primary/5 mix-blend-multiply blur-3xl filter transition-all duration-1000 ease-in-out dark:bg-primary/20 dark:mix-blend-screen"></div>
        <div className="animate-blob animation-delay-2000 absolute top-0 right-1/4 h-[400px] w-[400px] rounded-full bg-secondary/20 mix-blend-multiply blur-3xl filter transition-all duration-1000 ease-in-out dark:bg-secondary/20 dark:mix-blend-screen"></div>
        <div className="animate-blob animation-delay-4000 absolute -bottom-32 left-1/3 h-[600px] w-[600px] rounded-full bg-secondary/5 mix-blend-multiply blur-3xl filter transition-all duration-1000 ease-in-out dark:bg-secondary/20 dark:mix-blend-screen"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {}
          <div ref={textRef} className={`lg:col-span-7 transition-all duration-1000 ease-out ${isTextVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
            <h1 className="font-sans font-bold text-5xl leading-[1.1] tracking-tighter text-primary md:text-6xl lg:text-7xl xl:text-[5rem]">
              What is <br />
              <span className="italic text-secondary">Sleep Apnea?</span>
            </h1>
            
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
              Sleep apnea is more than just snoring—it&apos;s a serious medical condition where your breathing repeatedly stops and starts during sleep, impacting your heart health and daily vitality.
            </p>
            
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button size="lg" className="h-14 rounded-full bg-secondary px-8 text-base font-semibold text-secondary-foreground shadow-lg shadow-secondary/20 hover:bg-secondary/90" asChild>
                <Link to="/SleepApneaPage">Read the Signs</Link>
              </Button>
              <Button size="lg" variant="outline" className="h-14 rounded-full border-primary/20 bg-background/50 px-8 text-base text-primary backdrop-blur-sm hover:bg-accent/50 hover:text-primary">
                <Play className="mr-2 size-5 fill-current" />
                How It Works
              </Button>
            </div>
          </div>

          {}
          <div className="relative mt-12 lg:col-span-5 lg:mt-0">
            {}
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-tl-[120px] rounded-br-[120px] rounded-tr-[30px] rounded-bl-[30px] border border-border/50 bg-muted shadow-2xl md:aspect-square lg:aspect-[4/5]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612048/img/hero_physician_portrait.png" alt="Understanding airway health and sleep apnea symptoms" className="h-full w-full object-cover object-top" />
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 via-transparent to-transparent"></div>
            </div>

            {}
            <Card className="absolute -bottom-6 -left-4 w-[260px] animate-in fade-in slide-in-from-bottom-8 border-border/50 bg-card/95 shadow-xl backdrop-blur-md duration-1000 delay-500 fill-mode-both md:-left-12 lg:-left-16">
              <CardContent className="flex items-center gap-4 p-4">
                <div className="flex -space-x-3">
                  <div className="flex size-10 items-center justify-center overflow-hidden rounded-full border-2 border-card bg-muted">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612048/img/patient_avatar_female.png" alt="Patient" className="h-full w-full object-cover" />
                  </div>
                  <div className="flex size-10 items-center justify-center overflow-hidden rounded-full border-2 border-card bg-muted">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1612048/img/patient_avatar_male.png" alt="Patient" className="h-full w-full object-cover" />
                  </div>
                  <div className="flex size-10 items-center justify-center rounded-full border-2 border-card bg-secondary text-xs font-bold text-secondary-foreground">
                    +500
                  </div>
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-1">
                    <Star className="size-4 fill-secondary text-secondary" />
                    <span className="font-bold text-primary">4.9/5</span>
                  </div>
                  <span className="text-xs font-medium text-muted-foreground">
                    Patient Satisfaction
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
          
        </div>
      </div>
    </section>;
}